import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Delete, Lock, X } from 'lucide-react';
import { verifyPin } from '@/lib/api/pin.js';
import { parseApiError } from '@/lib/api/errors.js';

const PIN_LENGTH = 4;
const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back'];

export function PinDialog({
  open,
  title = 'Enter PIN',
  description = 'Enter your staff PIN to continue.',
  onVerified,
  onCancel,
}: {
  open: boolean;
  title?: string;
  description?: string;
  onVerified: () => void;
  onCancel: () => void;
}) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setPin('');
      setError('');
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (value: string) => verifyPin({ pin: value }),
    onSuccess: (result) => {
      if (result.valid) {
        onVerified();
        return;
      }
      if (result.locked) {
        setError('PIN locked due to too many attempts. Try again later.');
      } else {
        setError(
          result.attemptsRemaining != null
            ? `Incorrect PIN — ${result.attemptsRemaining} attempt(s) remaining`
            : 'Incorrect PIN',
        );
      }
      setPin('');
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      setError(parsed.message);
      setPin('');
    },
  });

  if (!open) return null;

  function submitPin(value: string) {
    if (value.length !== PIN_LENGTH || mutation.isPending) return;
    mutation.mutate(value);
  }

  function handleChange(next: string) {
    const digits = next.replace(/\D/g, '').slice(0, PIN_LENGTH);
    setPin(digits);
    setError('');
    if (digits.length === PIN_LENGTH) submitPin(digits);
  }

  function handleKeypad(key: string) {
    if (key === 'back') {
      setPin((p) => p.slice(0, -1));
      setError('');
      return;
    }
    if (!key) return;
    handleChange(pin + key);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    submitPin(pin);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') onCancel();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pin-dialog-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="rounded-xl bg-primary/10 p-2 text-primary">
              <Lock className="h-4 w-4" />
            </div>
            <div>
              <h2 id="pin-dialog-title" className="text-base font-bold text-slate-950">
                {title}
              </h2>
              <p className="text-xs text-slate-500">{description}</p>
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="mt-5" onSubmit={submit}>
          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={PIN_LENGTH}
            value={pin}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full rounded-xl border border-slate-300 px-3 py-3 text-center text-2xl tracking-[0.6em] outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            placeholder="••••"
          />

          {error ? <p className="mt-2 text-center text-sm font-semibold text-red-600">{error}</p> : null}

          <div className="mt-4 grid grid-cols-3 gap-2">
            {KEYPAD.map((key, i) =>
              key === '' ? (
                <span key={`empty-${i}`} />
              ) : (
                <button
                  key={key}
                  type="button"
                  className="flex h-12 items-center justify-center rounded-xl bg-slate-100 text-lg font-semibold text-slate-800 hover:bg-slate-200"
                  onClick={() => handleKeypad(key)}
                >
                  {key === 'back' ? <Delete className="h-5 w-5" /> : key}
                </button>
              ),
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={pin.length !== PIN_LENGTH || mutation.isPending}
            >
              {mutation.isPending ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
